import Image from 'next/image';
import DownArrowIcon from 'assets/svgs/DownArrowIcon';
import {
  defineLogoName,
  formatAirportDetails,
  formatElapsedTime,
  getTimeFromStringDateTime,
  renderArrivalDetails,
  renderStatusTextColor,
} from 'utils/utils';
import FlightRouteStop from 'components/FlightRouteStop';
import TransferIcon from 'assets/svgs/TransferIcon';
import DepartureIcon from 'assets/svgs/DepartureIcon';
import React, { useState } from 'react';
import { H2, H4, H5, H6, P } from 'components/Text';
import { ArrivalOrDeparture, FlightSegment, Leg } from 'interfaces/flights';
import ArrivalIcon from 'assets/svgs/ArrivalIcon';
import { useTranslation } from 'next-i18next/pages';
import { FlightDetails } from 'interfaces/flightDetails';
import Extras from 'components/Extras';
import { Currencies } from 'interfaces/payment';

interface FlightRouteIF {
  departure?: ArrivalOrDeparture;
  arrival?: ArrivalOrDeparture;
  marketinAirlineName?: string;
  elapsedTime?: string;
  flightSegments?: FlightSegment[] | null;
  fromCountry?: string;
  toCountry?: string;
  containerClassName?: string;
  orderStatus?: string;
  pnr?: string;
  ticket_number?: string;
  showDetails?: boolean;
  showDownArrow?: boolean;
  alternateDate?: string;
  flightDetails?: FlightDetails;
  currency?: Currencies;
  currentLeg?: Leg;
  isCartPackagesModal?: boolean;
  showAirportName?: boolean;
  joinDetails?: boolean;
}

const FlightRoute: React.FC<FlightRouteIF> = ({
  departure,
  arrival,
  marketinAirlineName,
  elapsedTime,
  flightSegments,
  fromCountry,
  toCountry,
  containerClassName,
  pnr,
  orderStatus,
  ticket_number,
  showDetails,
  alternateDate,
  showDownArrow = true,
  flightDetails,
  currency,
  currentLeg,
  isCartPackagesModal = false,
  showAirportName = true,
  joinDetails = false,
}) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const renderFlightNumber = (
    airline: string,
    flightNumber: string,
    newAirLine?: string,
    newFlightNumber?: string,
  ) => {
    return (
      <div className="mb-2">
        <H6
          className={`text-tt-light-gray-purple  ${
            newAirLine && 'line-through'
          }`}>
          {airline}{' '}
          {newAirLine && (
            <span className="text-green-600">
              {t('New airline')}: {newAirLine}
            </span>
          )}
        </H6>
        <H6
          className={`text-tt-light-gray-purple  ${
            newFlightNumber && 'line-through'
          }`}>
          {flightNumber}{' '}
          {newFlightNumber && (
            <span className="text-green-600">
              {t('New flight nr')}: {newFlightNumber}
            </span>
          )}
        </H6>
      </div>
    );
  };

  return (
    <div className={containerClassName || ''} onClick={() => setOpen(!open)}>
      {/* Airline */}
      <div className="flex flex-col xs:flex-row w-full justify-between xs:items-center">
        <div className="w-[170px] h-[50px] relative -ml-5">
          {marketinAirlineName && (
            <Image
              fill
              alt={marketinAirlineName}
              src={defineLogoName(marketinAirlineName.toLocaleLowerCase())}
            />
          )}
        </div>
        <div className="flex">
          <H5 className="font-bold pr-4">{alternateDate}</H5>
          {showDownArrow && (
            <DownArrowIcon
              color={open ? 'var(--tt-yellow)' : undefined}
              className={`h-5 w-5 ${open && 'rotate-180'}`}
            />
          )}
        </div>
      </div>
      {/* Start and endpoint of the flight */}
      <div className="flex mt-4 flex-col sm:flex-row">
        <div className="flex sm:flex-col sm:flex-1 p-2 pl-0">
          <div className="flex justify-center flex-col sm:flex-row sm:items-center dm-san">
            <H2 className="tt-gopher font-bold sm:pr-2">
              {getTimeFromStringDateTime(departure?.dateTime)}
            </H2>
            <div className="flex h-full text-tt-light-gray-purple sm:hidden">
              <div className="ml-4 border-l border-tt-light-gray-purple-line min-h-full h-4" />
            </div>
            <H5 className="hidden sm:block w-full text-center whitespace-nowrap mt-2 mb-2 border-b border-tt-light-gray-purple-line leading-[0.1rem] text-tt-light-gray-purple">
              <span
                className={`${
                  isCartPackagesModal ? 'bg-tt-white-purple' : 'bg-white'
                } dm-san`}>
                {formatElapsedTime(elapsedTime, t)}
              </span>
            </H5>
          </div>
          <div className="flex flex-col flex-1 min-w-0 dm-san pl-2 grow sm:pl-0 sm:pl-0 sm:flex-1">
            <div className="sm:block">
              <P className="w-full">
                {formatAirportDetails(departure, showAirportName)}
              </P>
              {fromCountry && <H6>{t(fromCountry)}</H6>}
            </div>
          </div>
        </div>
        <div className="text-tt-light-gray-purple sm:hidden">
          {formatElapsedTime(elapsedTime, t)}
        </div>
        <div className="flex flex-col flex-1">
          <div className="flex flex-col justify-start dm-san sm:hidden">
            <div className="flex h-full text-tt-light-gray-purple">
              <div className="ml-4 border-l border-tt-light-gray-purple-line min-h-full h-4" />
            </div>
          </div>
          <div className="flex sm:flex-col sm:flex-1 sm:p-2 sm:pl-0">
            <H2 className="tt-gopher font-bold">
              {getTimeFromStringDateTime(arrival?.dateTime)}
            </H2>
            <div className="flex flex-col flex-1 min-w-0 grow pl-2 sm:grow-0 sm:pl-0 dm-san">
              <P className="break-words">
                {formatAirportDetails(arrival, showAirportName)}
              </P>
              {toCountry && <H6>{t(toCountry)}</H6>}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        {orderStatus && (
          <H5 className={`w-full ${renderStatusTextColor(orderStatus)}`}>
            {t(`${orderStatus}`)}
          </H5>
        )}
        {pnr && <H5 className="w-full text-tt-light-gray-purple">PNR: {pnr}</H5>}
        {ticket_number && (
          <H5 className="w-full text-tt-light-gray-purple">
            {t('Ticket number')}: {ticket_number}
          </H5>
        )}
      </div>
      {showDetails && open && (
        <div
          className={`flex flex-col mt-4 text-base dm-sans ${
            isCartPackagesModal ? 'bg-tt-white-purple' : 'bg-white'
          }`}>
          {flightSegments &&
            flightSegments?.map((fs: FlightSegment, f_idx: number) => (
              <div
                key={`${f_idx}-${fs.departure.airport.name}-${fs.departure.dateTime}-${fs.flightNumber}-${fs.arrival.airport.name}-${fs.arrival.dateTime}`}>
                {/* Departure */}
                <FlightRouteStop
                  joinDetails={
                    joinDetails
                      ? [marketinAirlineName || '', fs.flightNumber]
                      : undefined
                  }
                  newDate={
                    fs.departure.dateTime !== fs.departure.newDateTime
                      ? fs.departure.newDateTime
                      : undefined
                  }
                  dateTime={fs.departure.dateTime}
                  time={getTimeFromStringDateTime(fs.departure.dateTime) || ''}
                  title={
                    formatAirportDetails(fs.departure, showAirportName) || ''
                  }
                  elapsedTime={fs.elapsedTime}
                  icon={
                    f_idx === 0 ? (
                      <DepartureIcon
                        className="w-5 h-5"
                        secondaryColor="var(--tt-dark-purple)"
                      />
                    ) : (
                      <DepartureIcon
                        className="w-5 h-5"
                        secondaryColor="var(--tt-dark-purple)"
                      />
                    )
                  }
                  details={
                    !joinDetails
                      ? renderFlightNumber(
                          fs.marketingAirlineName,
                          fs.flightNumber,
                          fs.newAirlineName !== fs.newAirlineName
                            ? fs.newAirlineName
                            : undefined,
                          fs.newFlightNumber !== fs.newFlightNumber
                            ? fs.newFlightNumber
                            : undefined,
                        )
                      : undefined
                  }
                />
                {/* Arrival */}
                <FlightRouteStop
                  joinDetails={
                    joinDetails
                      ? renderArrivalDetails(
                          fs,
                          f_idx,
                          flightSegments ? flightSegments?.length - 1 : null,
                          t,
                        )
                      : undefined
                  }
                  newDate={
                    fs.arrival.dateTime !== fs.arrival.newDateTime
                      ? fs.arrival.newDateTime
                      : undefined
                  }
                  elapsedTime={null}
                  lastFlightStop={
                    f_idx ===
                    (flightSegments?.length ? flightSegments?.length - 1 : null)
                  }
                  dateTime={fs.arrival.dateTime}
                  time={getTimeFromStringDateTime(fs.arrival.dateTime) || ''}
                  title={
                    formatAirportDetails(fs.arrival, showAirportName) || ''
                  }
                  icon={
                    f_idx ===
                    (flightSegments?.length
                      ? flightSegments?.length - 1
                      : null) ? (
                      <ArrivalIcon
                        className="w-5 h-5"
                        secondaryColor="var(--tt-dark-purple)"
                      />
                    ) : (
                      <TransferIcon className="w-5 h-5" />
                    )
                  }
                />
              </div>
            ))}

          {/* Selected packagesa at cart page */}
          {flightDetails && currency && currentLeg && (
            <Extras
              bgColor={isCartPackagesModal ? 'bg-white' : undefined}
              totalPrice={
                flightDetails.result.total_fare_in_preferred_currencies
              }
              hidePrice
              hideSelect
              key={flightDetails.result.combinations[0].bookingId}
              currency={currency}
              width="w-[100%]"
              handleSelect={() => {}}
              outboundCities={`${departure?.city.code} - ${arrival?.city.code}`}
              outboundFareFamily={
                currentLeg.flightSegments[0].default_fare_family
              }
              outboundFareFamilyDesc={
                currentLeg.flightSegments[0].default_fare_family_description
              }
              returnCities=""
              returnFareFamily=""
              bookingId={flightDetails.result.combinations[0].bookingId}
              showReturnFareFamily={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FlightRoute;
